import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { useRef } from "react";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsGrid, Trash01 } from "@untitledui/icons";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { ListItem } from "@/components/Molecules/ListItem/ListItem";
import { Button } from "@/components/base/buttons/button";
import {
	Dropdown,
	DropdownSectionHeaderOrder,
} from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useAdminFormEditorPaths } from "../hooks/use-admin-form-editor-paths";
import { alphabetizeFieldOptions } from "../lib/field-option-order";

interface FieldOption {
	value: string;
	label: string;
}

interface FieldOptionListEditorProps {
	fieldId: string;
	options: FieldOption[];
	/** Drag reorder and one-shot alphabetize. Radio leaves this false. */
	reorderable: boolean;
	onChange: (options: FieldOption[]) => void;
}

function readTextFieldChangeValue(value: string | { target?: { value?: string } }): string {
	if (typeof value === "string") return value;
	return value.target?.value ?? "";
}

let optionRowSequence = 0;

function createOptionRowId(fieldId: string): string {
	optionRowSequence += 1;
	return `${fieldId}-row-${optionRowSequence}`;
}

function alignOptionRowIds(
	ids: string[],
	count: number,
	fieldId: string,
): string[] {
	if (ids.length === count) return ids;
	if (ids.length > count) return ids.slice(0, count);
	return [
		...ids,
		...Array.from({ length: count - ids.length }, () =>
			createOptionRowId(fieldId),
		),
	];
}

function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
	const next = [...items];
	const [moved] = next.splice(from, 1);
	if (!moved) return [...items];
	next.splice(to, 0, moved);
	return next;
}

function optionSignature(option: FieldOption | undefined): string {
	return `${option?.value ?? ""}\0${option?.label ?? ""}`;
}

function signaturesAreUnique(options: readonly FieldOption[]): boolean {
	const seen = new Set<string>();
	for (const option of options) {
		const signature = optionSignature(option);
		if (seen.has(signature)) return false;
		seen.add(signature);
	}
	return true;
}

/** Follows option text when every label/value pair is unique. Duplicate text keeps the current row ids. */
export function remapOptionRowIds(
	previous: readonly FieldOption[],
	options: readonly FieldOption[],
	rowIds: readonly string[],
): string[] | null {
	if (
		rowIds.length !== options.length ||
		previous.length !== options.length ||
		options.length === 0 ||
		!signaturesAreUnique(previous) ||
		!signaturesAreUnique(options)
	) {
		return null;
	}
	const unused = rowIds.map((id, index) => ({
		id,
		signature: optionSignature(previous[index]),
	}));
	const remapped: string[] = [];
	for (const option of options) {
		const match = unused.findIndex(
			(entry) => entry.signature === optionSignature(option),
		);
		if (match < 0) return null;
		const matched = unused[match];
		unused.splice(match, 1);
		if (!matched) return null;
		remapped.push(matched.id);
	}
	return remapped;
}

export function FieldOptionListEditor({
	fieldId,
	options,
	reorderable,
	onChange,
}: FieldOptionListEditorProps) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);
	const rowIdsRef = useRef<string[]>([]);
	const fieldKeyRef = useRef(fieldId);
	const ownedOptionsRef = useRef(options);
	if (fieldKeyRef.current !== fieldId) {
		fieldKeyRef.current = fieldId;
		rowIdsRef.current = [];
		ownedOptionsRef.current = options;
	}
	if (
		options !== ownedOptionsRef.current ||
		rowIdsRef.current.length !== options.length
	) {
		const previous = ownedOptionsRef.current;
		const sameOptions =
			previous.length === options.length &&
			options.every((option) => previous.includes(option));
		if (sameOptions && rowIdsRef.current.length === options.length) {
			rowIdsRef.current = options.map((option) => {
				const index = previous.indexOf(option);
				return rowIdsRef.current[index] ?? createOptionRowId(fieldId);
			});
		} else if (
			rowIdsRef.current.length === options.length &&
			options.length > 0 &&
			options.every((option) => !previous.includes(option))
		) {
			const remapped = remapOptionRowIds(
				previous,
				options,
				rowIdsRef.current,
			);
			if (remapped) rowIdsRef.current = remapped;
		} else if (rowIdsRef.current.length !== options.length) {
			rowIdsRef.current = alignOptionRowIds(
				rowIdsRef.current,
				options.length,
				fieldId,
			);
		}
		ownedOptionsRef.current = options;
	}
	const rowIds = rowIdsRef.current;

	const updateLabel = (index: number, raw: string) => {
		const next = [...options];
		const current = next[index];
		if (!current) return;
		next[index] = {
			...current,
			label: raw,
			value: raw,
		};
		onChange(next);
	};

	const removeAt = (index: number) => {
		const next = options.filter((_, optionIndex) => optionIndex !== index);
		rowIdsRef.current = rowIds.filter((_, optionIndex) => optionIndex !== index);
		ownedOptionsRef.current = next;
		onChange(next);
	};

	const addOption = () => {
		const nextIndex = options.length + 1;
		const text = String(
			t(paths.formEditor.properties.newOptionLabel, { index: nextIndex }),
		);
		const next = [...options, { value: text, label: text }];
		rowIdsRef.current = [...rowIds, createOptionRowId(fieldId)];
		ownedOptionsRef.current = next;
		onChange(next);
	};

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const from = rowIds.indexOf(String(active.id));
		const to = rowIds.indexOf(String(over.id));
		if (from < 0 || to < 0 || from === to) return;
		const next = moveItem(options, from, to);
		rowIdsRef.current = moveItem(rowIds, from, to);
		ownedOptionsRef.current = next;
		onChange(next);
	};

	const alphabetize = () => {
		const order = options
			.map((_, index) => index)
			.sort((a, b) =>
				(options[a]?.label ?? "").localeCompare(
					options[b]?.label ?? "",
					undefined,
					{ sensitivity: "base" },
				),
			);
		const next = alphabetizeFieldOptions(options);
		rowIdsRef.current = order.map(
			(index) => rowIds[index] ?? createOptionRowId(fieldId),
		);
		ownedOptionsRef.current = next;
		onChange(next);
	};

	const orderAzLabel =
		t(paths.formEditor.properties.orderAz) ||
		t(paths.formEditor.properties.alphabetizeOptions);

	const rows = options.map((option, index) => {
		const rowId = rowIds[index] ?? `${fieldId}-missing-${index}`;
		return reorderable ? (
			<SortableOptionRow
				key={rowId}
				rowId={rowId}
				option={option}
				index={index}
				total={options.length}
				canRemove={options.length > 1}
				onLabelChange={(raw) => updateLabel(index, raw)}
				onRemove={() => removeAt(index)}
			/>
		) : (
			<PlainOptionRow
				key={rowId}
				option={option}
				index={index}
				total={options.length}
				canRemove={options.length > 1}
				onLabelChange={(raw) => updateLabel(index, raw)}
				onRemove={() => removeAt(index)}
			/>
		);
	});

	return (
		<fieldset className="flex min-w-0 flex-col gap-(--spacing-md,8px) border-0 p-0">
			{reorderable ? (
				<>
					<legend className="sr-only">
						{t(paths.formEditor.properties.options)}
					</legend>
					<Dropdown.SectionHeader
						order={DropdownSectionHeaderOrder.Search}
						title={t(paths.formEditor.properties.options)}
						showAction
						actionLabel={orderAzLabel}
						actionAriaLabel={orderAzLabel}
						onAction={alphabetize}
					/>
				</>
			) : (
				<legend className="text-sm font-medium text-text-primary">
					{t(paths.formEditor.properties.options)}
				</legend>
			)}
			{reorderable ? (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={onDragEnd}
				>
					<SortableContext
						items={rowIds}
						strategy={verticalListSortingStrategy}
					>
						<div className="flex flex-col">{rows}</div>
					</SortableContext>
				</DndContext>
			) : (
				<div className="flex flex-col gap-2">{rows}</div>
			)}
			<Button
				size="sm"
				color="secondary"
				type="button"
				className={reorderable ? "w-full" : undefined}
				onClick={addOption}
			>
				{t(paths.formEditor.properties.addOption)}
			</Button>
		</fieldset>
	);
}

function PlainOptionRow({
	option,
	index,
	total,
	canRemove,
	onLabelChange,
	onRemove,
}: {
	option: FieldOption;
	index: number;
	total: number;
	canRemove: boolean;
	onLabelChange: (value: string) => void;
	onRemove: () => void;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	return (
		<div className="flex gap-2">
			<Input
				aria-label={t(paths.formEditor.properties.optionLabelField, {
					index: index + 1,
					total,
				})}
				className="min-w-0 flex-1"
				value={option.label}
				onChange={(value) => onLabelChange(readTextFieldChangeValue(value))}
			/>
			<Button
				size="sm"
				color="tertiary"
				type="button"
				isDisabled={!canRemove}
				aria-label={t(paths.formEditor.properties.removeOptionNamed, {
					label: option.label,
				})}
				onClick={onRemove}
			>
				{t(paths.formEditor.properties.removeOption)}
			</Button>
		</div>
	);
}

function SortableOptionRow({
	rowId,
	option,
	index,
	total,
	canRemove,
	onLabelChange,
	onRemove,
}: {
	rowId: string;
	option: FieldOption;
	index: number;
	total: number;
	canRemove: boolean;
	onLabelChange: (value: string) => void;
	onRemove: () => void;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const sortable = useSortable({ id: rowId });
	const style = {
		transform: CSS.Transform.toString(sortable.transform),
		transition: sortable.transition,
	};

	return (
		<div ref={sortable.setNodeRef} style={style} data-option-row={rowId}>
			<ListItem
				type="list"
				alignment="middle"
				showOverline={false}
				primary=""
				leading={
					<button
						type="button"
						className="inline-flex shrink-0 items-center text-primary"
						aria-label={t(paths.formEditor.properties.dragOption, {
							label: option.label,
						})}
						{...sortable.attributes}
						{...sortable.listeners}
					>
						<DotsGrid className="size-6" aria-hidden />
					</button>
				}
				supportingElements={
					<div className="min-w-0 w-full flex-1">
						<Input
							aria-label={t(paths.formEditor.properties.optionLabelField, {
								index: index + 1,
								total,
							})}
							className="w-full"
							value={option.label}
							onChange={(value) =>
								onLabelChange(readTextFieldChangeValue(value))
							}
						/>
					</div>
				}
				trailing={
					<IconButton
						hierarchy="link"
						size="sm"
						shape="round"
						icon={Trash01}
						aria-label={t(paths.formEditor.properties.removeOptionNamed, {
							label: option.label,
						})}
						isDisabled={!canRemove}
						onPress={onRemove}
					/>
				}
			/>
		</div>
	);
}
